import {
  Dialog,
  DialogBareContent,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Skeleton,
  useErrorToast,
} from '@h2hspark/ui';
import { RefreshCw, X } from 'lucide-react';
import * as React from 'react';

import { useQrCodeSource } from '../../member/use-qr-code-source';

import { useMyConnectCode, useReplaceMyConnectCode } from './api';
import {
  MY_CODE_ALTERNATIVE_TEXT,
  MY_CODE_CLOSE_LABEL,
  MY_CODE_HEADING,
  MY_CODE_SUPPORTING_LINE,
  MY_CODE_WORKS_ANYWHERE,
  REPLACE_CODE_BUSY_LABEL,
  REPLACE_CODE_CONFIRM_LABEL,
  REPLACE_CODE_CONSEQUENCE,
  REPLACE_CODE_DONE,
  REPLACE_CODE_LABEL,
  REPLACE_CODE_PROMPT,
} from './constants';

/**
 * **The member's profile connect code, as an overlay** — `Web App.dc.html`'s `myQrOpen`
 * (lines 1150–1161), and the one screen ADR 053's decision is visible on.
 *
 * §2.16's card: a `--purple-800` box at 380px with a round close control, the uppercase eyebrow,
 * the code on a **white tile**, the member's name and role beneath it, and the supporting line.
 * The white tile is a scanning requirement rather than a style — a QR needs a light quiet zone on
 * all four sides to be found at all — and it is the same treatment the event's Connect card uses,
 * through the same {@link useQrCodeSource}.
 *
 * `MEM-057` AC-5: **the code shows at every width.** It is the scanner that is phone-only, and
 * that gate is a device check rather than a breakpoint (`responsive-behaviour.ts`). So this dialog
 * has no width condition at all, and is the full-screen purple treatment on a phone and the 380px
 * card above `md`, exactly as the ticket is.
 *
 * ## Two things the prototype does not draw, and why they are here
 *
 * **What this code is not.** A member who has used Connect at an event has seen a code that looked
 * like this one and behaved differently — it closed with the evening, and whoever scanned it
 * appeared in *People you met*. This one has no event and no end, and scanning it connects
 * immediately with no request to accept. `MY_CODE_WORKS_ANYWHERE` says so, on the dialog, because
 * a member cannot consent to showing a code whose reach they have to infer.
 *
 * **Replace my code.** ADR 053: replacement is this code's *only* revocation, and a revocation
 * nobody can find is not one. It is here, beside the code, rather than behind a settings row —
 * the member who needs it is the member looking at a code they think has got out. The confirm
 * step states the consequence in the two directions that matter: every saved copy stops working,
 * and connections already made are untouched.
 */
export function MyConnectionCodeDialog({
  fullName,
  roleLine,
  children,
}: {
  readonly fullName: string;
  /** *Founder, H2HSpark* — the same line the account card carries. `null` when there is none. */
  readonly roleLine: string | null;
  /** The control that opens it, rendered as the trigger (`asChild`). */
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay className="z-[60] bg-[rgba(28,14,48,.6)] backdrop-blur-none" />
        <DialogBareContent
          data-testid="my-connection-code-dialog"
          className="fixed inset-0 z-[61] flex flex-col items-center overflow-y-auto bg-brand-800 px-6 pb-8 pt-14 text-center text-white md:inset-x-0 md:bottom-auto md:top-6 md:mx-auto md:w-full md:max-w-[380px] md:rounded-xl md:p-7 md:pt-7 md:shadow-xl"
        >
          <DialogClose
            aria-label={MY_CODE_CLOSE_LABEL}
            className="absolute right-4 top-4 inline-flex h-[34px] w-[34px] items-center justify-center rounded-pill bg-white/20 text-white"
          >
            <X className="h-[18px] w-[18px]" aria-hidden />
          </DialogClose>
          <CodeContents fullName={fullName} roleLine={roleLine} />
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}

function CodeContents({
  fullName,
  roleLine,
}: {
  readonly fullName: string;
  readonly roleLine: string | null;
}): React.ReactElement {
  const surface = useMyConnectCode();
  const replace = useReplaceMyConnectCode();
  const showError = useErrorToast();
  const [confirming, setConfirming] = React.useState(false);

  React.useEffect(() => {
    if (replace.error !== null) showError(replace.error, 'That did not replace. Try again.');
  }, [replace.error, showError]);

  const code = surface.data?.surface.code ?? null;
  const qrSource = useQrCodeSource(code);

  return (
    <div className="w-full max-w-[300px] md:max-w-none">
      <DialogTitle className="font-brand text-2xs font-bold uppercase tracking-[0.14em] text-fuchsia-200">
        {MY_CODE_HEADING}
      </DialogTitle>

      {/* The white tile. It is the quiet zone, so it is drawn whether or not the code has
          arrived — a skeleton inside it rather than in place of it, so the dialog does not
          change size when the request lands under somebody's thumb. */}
      <div className="mx-auto mt-4 w-[220px] max-w-full rounded-md bg-white p-3.5 shadow-md">
        {qrSource === null ? (
          <Skeleton className="aspect-square w-full rounded" />
        ) : (
          <img src={qrSource} alt={MY_CODE_ALTERNATIVE_TEXT} className="h-auto w-full" />
        )}
      </div>

      <p className="mt-[18px] font-brand text-lg font-extrabold text-white">{fullName}</p>
      {roleLine === null ? null : <p className="text-sm text-white/85">{roleLine}</p>}
      <p className="mt-[18px] text-xs leading-snug text-white/80">{MY_CODE_SUPPORTING_LINE}</p>
      <p className="mt-2.5 text-xs leading-snug text-white/70">{MY_CODE_WORKS_ANYWHERE}</p>

      <div className="mt-5 border-t border-white/15 pt-4">
        {confirming ? (
          <div className="space-y-3">
            <p className="font-brand text-sm font-bold text-white">{REPLACE_CODE_PROMPT}</p>
            <p className="text-xs leading-snug text-white/75">{REPLACE_CODE_CONSEQUENCE}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                }}
                className="flex-1 rounded-pill border-[1.5px] border-white/35 px-4 py-2.5 font-brand text-sm font-bold text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={replace.isPending}
                onClick={() => {
                  replace.mutate(undefined, { onSuccess: () => setConfirming(false) });
                }}
                className="flex-1 rounded-pill bg-white px-4 py-2.5 font-brand text-sm font-bold text-brand-800 disabled:opacity-60"
              >
                {replace.isPending ? REPLACE_CODE_BUSY_LABEL : REPLACE_CODE_CONFIRM_LABEL}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setConfirming(true);
            }}
            className="inline-flex items-center gap-2 rounded-pill bg-white/15 px-3.5 py-2.5 font-brand text-xs font-bold text-white"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {REPLACE_CODE_LABEL}
          </button>
        )}

        {/* `role="status"`, so a member using a screen reader is told the revocation landed. The
            code above has already changed by the time this renders — the mutation writes the new
            surface straight into the cache rather than invalidating it, so there is no moment
            where the member has revoked their code and is looking at nothing. */}
        {replace.isSuccess && !confirming ? (
          <p role="status" className="mt-3 text-xs leading-snug text-fuchsia-200">
            {REPLACE_CODE_DONE}
          </p>
        ) : null}
      </div>
    </div>
  );
}
